// src/follower/components/FollowerBotPanel.jsx
// Fork of BotIntelligencePanel.jsx — adapted for followerMarketEvent flat payload.
//
// KEY DIFFERENCES FROM MASTER BotIntelligencePanel:
//   1. No extractAgentStory() — reads flat eval_* fields directly from followerBars
//   2. No thinking arc (initial/combined/final) — ConfidenceArc removed
//   3. No rule votes / evidence weights — VoteBars / WeightBars removed
//   4. No model DRL/DT/leadership/timingSync — model internals not in follower event
//   5. No OverridePanel (close position / pause bot) — replaced by follower GuardrailsPanel controls
//   6. Position block adapted for servantPositionEvent shape (contract_symbol, option_type,
//      entry_fill_price, qty, realized_pnl — not master equity positions)
//   7. Options strategy shown read-only (from eval_option_strategy) — follower cannot act on it
//   8. MarketWorldModel stripped of DRL/DT/consensus/timing rows — kept price bar + regime chips
//
// IDENTICAL TO MASTER:
//   - Three-state machine: watching → active → post_trade
//   - Status orb + headline + subheadline (same deterministic rules)
//   - Today stats (evaluated / proceeded / skipped) — pure eval_decision scan
//   - Dashboard / Analysis view toggle
//   - Market context price bar (S/R + entry + target + stop dot)
//   - Regime chips: day type, volatility, flip rate, stability, CI, DER
//   - Confidence + EV + timeframe lock metric cards
//   - Entry status / cooldown banner
//   - Reactive history trend strip (last 6 eval checks)
//   - Pattern row (simplified — name + confidence)
//   - Memory row
//
// Props:
//   followerBars     — merged bar array (from followerMarketEvent + tradeEvent)
//   currentPrice     — number from tradeEvent.close
//   openPositions    — array from servantPositionEvent (status=open)
//   closedPositions  — array from servantPositionEvent (status=closed), sorted oldest first
//   metrics          — { winRate, closedTrades } derived from closed positions
//   onPauseBot       — callback to follower pause route (replaces onClosePosition/onPauseBot)

import { useState, useEffect, useRef, useMemo } from "react";

// ─── FONTS ────────────────────────────────────────────────────────────────────
const FONTS = `@import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=Syne:wght@600;700;800&display=swap');`;

// ─── THEME ────────────────────────────────────────────────────────────────────
const C = {
    bg:      "#08101E",
    surface: "#0C1525",
    s2:      "#101B2E",
    s3:      "#0e1828",
    border:  "#1E2D42",
    b2:      "#2a3d56",
    text:    "#E8EDF5",
    muted:   "#6A7D95",
    subtle:  "#1E2D42",
    body:    "#B8C8DC",
    green:   "#00E896",
    cyan:    "#00C8FF",
    amber:   "#FFB340",
    red:     "#FF4466",
    blue:    "#4488FF",
    purple:  "#9966FF",
    gDim:    "rgba(0,232,150,0.10)",
    cDim:    "rgba(0,200,255,0.10)",
    aDim:    "rgba(255,179,64,0.10)",
    rDim:    "rgba(255,68,102,0.10)",
    bDim:    "rgba(68,136,255,0.10)",
};

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const fmt2 = (v) => typeof v === "number" ? v.toFixed(2) : "—";
const pct  = (v) => `${Math.round((v || 0) * 100)}%`;

const confColor = (b) =>
({ VERY_LOW: C.red, LOW: C.amber, MEDIUM: C.cyan, HIGH: C.green, VERY_HIGH: C.green })[b] || C.muted;

const bucketFromScore = (score) => {
    if (score == null) return null;
    if (score >= 0.80) return "VERY_HIGH";
    if (score >= 0.65) return "HIGH";
    if (score >= 0.45) return "MEDIUM";
    if (score >= 0.25) return "LOW";
    return "VERY_LOW";
};

const dayTypeColor = (dt) => ({
    TREND_DAY:        C.green,
    STRONG_TREND_DAY: C.green,
    EVENT_TREND:      C.cyan,
    EVENT_DAY:        C.cyan,
    WEAK_TREND_DAY:   "#88D400",
    RANGE_DAY:        C.blue,
    SIDEWAYS_DAY:     C.purple,
    CHOPPY_DAY:       C.red,
    VOLATILE_DAY:     C.amber,
})[dt] || C.muted;

const dayTypeBg = (dt) => dayTypeColor(dt) + "18";

const stabilityColor = (s) =>
    s == null ? C.muted : s >= 0.7 ? C.green : s >= 0.4 ? C.amber : C.red;

// ─── FLAT EVAL EXTRACTOR ──────────────────────────────────────────────────────
// Reads followerMarketEvent flat eval_* fields.
// Returns null if bar has no eval data.
// Replaces extractAgentStory() from master entirely.
function extractFlatEval(bar) {
    if (!bar || !bar.eval_decision) return null;
    const confBucket = bar.eval_confidence_bucket ||
        bucketFromScore(bar.eval_confidence) || null;
    return {
        decision:         bar.eval_decision,
        confidence:       bar.eval_confidence ?? null,
        confBucket,
        evalType:         bar.eval_type || "PROACTIVE",
        path:             bar.eval_execution_path || null,
        positionId:       bar.eval_position_id || bar.position_id || null,
        timestamp:        bar.t || null,
        narrative:        bar.eval_narrative_summary || null,
        // Regime context
        regimeCtx: {
            dayType:          bar.eval_day_type || null,
            entryType:        bar.eval_entry_type || null,
            entryAllowed:     bar.eval_entry_allowed ?? null,
            entryBlockReason: bar.eval_entry_block_reason || null,
            eventDay:         bar.eval_event_day ?? false,
            volatilityCtx:    bar.eval_volatility_context || null,
            flipRate:         bar.eval_flip_rate ?? null,
            der:              bar.eval_der ?? null,
            ci14:             bar.eval_ci14 ?? null,
            ci6:              bar.eval_ci6 ?? null,
            stabilityScore:   bar.eval_stability_score ?? null,
            scaleCap:         bar.eval_scale_cap ?? null,
            stopFloorPct:     bar.eval_stop_floor_pct ?? null,
            cooldownActive:   bar.eval_cooldown_active ?? false,
            rrFloor:          bar.eval_rr_floor ?? null,
        },
        // Position-specific
        rrPreview:        bar.eval_rr_preview ?? null,
        newTarget:        bar.eval_new_target ?? null,
        newStop:          bar.eval_new_stop_loss ?? null,
        supportLevel:     bar.eval_support_level ?? null,
        resistanceLevel:  bar.eval_resistance_level ?? null,
        mindChanged:      bar.eval_mind_changed ?? false,
        // Intelligence
        timeframeLock: {
            active:       bar.eval_timeframe_lock ?? false,
            remainingMin: bar.eval_timeframe_lock_min ?? 0,
        },
        memory: {
            cases:       bar.eval_memory_cases ?? 0,
            similarity:  bar.eval_memory_similarity ?? 0,
            successRate: bar.eval_memory_success_rate ?? null,
        },
        performance: {
            ev:       bar.eval_expected_value ?? null,
            supports: bar.eval_supports_decision ?? true,
        },
        riskFlagCount:  bar.eval_risk_flag_count ?? 0,
        patternName:    bar.eval_pattern_name || null,
        patternConf:    bar.eval_pattern_confidence ?? null,
        patternRel:     bar.eval_pattern_reliability ?? null,
        optionStrategy: bar.eval_option_strategy || null,
        flags:          [],  // flat event doesn't carry flag strings — omit
    };
}

// ─── TOOLTIP ──────────────────────────────────────────────────────────────────
const TIPS = {
    "Flip Rate":      "How often the market regime switches between trending and sideways. High flip rate = unstable conditions where bot signals are less reliable.",
    "Timeframe Lock": "Bot's own rule preventing early exit. Gives the trade time to develop before reassessing. No early exit until the lock expires.",
    "EV":             "Expected Value — average profit from similar historical trades. Negative EV means history is mixed, but the bot is holding by its rules.",
    "Formation":      "How completely the candlestick pattern has formed. Below 70% means it's partially formed and less reliable.",
    "Stability Score":"Composite market stability score (0–1). High = clean, predictable conditions. Low = noisy, regime-unstable environment.",
    "Chop Index":     "Choppiness Index — below 38.2 = strong trend, above 61.8 = choppy range. CI-14 is the longer read, CI-6 is more reactive.",
    "DER":            "Directional Efficiency Ratio — how much of the bar's range was directional vs. choppy. ≥0.7 = efficient trend.",
    "Risk Params":    "Active session constraints. Scale Cap limits position size. Stop Floor sets minimum stop distance. RR Floor is the minimum risk/reward required to enter.",
    "Day Type":       "Bot's classification of the current session. EVENT TREND and TREND DAY are highest-quality entry environments. CHOPPY and VOLATILE trigger extra caution.",
    "Entry Window":   "Early entry window is open at the start of each session. Once closed, only standard entries qualify.",
    "Memory":         "Number of similar historical setups found. More cases with high similarity = more reliable signal.",
};

function Tooltip({ text, children }) {
    const [show, setShow] = useState(false);
    const [pos, setPos]   = useState({ x: 0, y: 0 });
    const timer = useRef(null);
    const tip = TIPS[text];
    if (!tip) return children;
    return (
        <span
            style={{ position: "relative", display: "inline-flex" }}
            onMouseEnter={(e) => {
        const x = e.clientX; const y = e.clientY;
        timer.current = setTimeout(() => { setPos({ x, y }); setShow(true); }, 300);
      }}
            onMouseLeave={() => { clearTimeout(timer.current); setShow(false); }}
        >
      {children}
      <span style={{
        position: "fixed", pointerEvents: "none", zIndex: 9999,
        left: pos.x + 12, top: pos.y - 8,
        opacity: show ? 1 : 0, transition: "opacity 0.15s",
        maxWidth: "260px", background: C.s3, border: `1px solid ${C.b2}`,
        borderRadius: "8px", padding: "8px 12px", fontSize: "11px",
        color: C.text, lineHeight: 1.6, boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
      }}>
        <span style={{ fontWeight: 700, color: C.cyan, fontSize: "10px",
          letterSpacing: "0.5px", fontFamily: "'DM Mono', monospace" }}>{text}</span>
        <br />{tip}
      </span>
    </span>
    );
}

// ─── STATUS ORB ───────────────────────────────────────────────────────────────
function StatusOrb({ color, pulse, size = 48 }) {
    const anim = pulse === "slow"
        ? "fbpOrbSlow 3s ease-in-out infinite"
        : pulse === "medium"
        ? "fbpOrbMed 2s ease-in-out infinite"
        : "none";
    return (
        <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
            <style>{`
        @keyframes fbpOrbSlow { 0%,100%{box-shadow:0 0 0 0 ${color}40} 50%{box-shadow:0 0 0 ${size*0.4}px ${color}00} }
        @keyframes fbpOrbMed  { 0%,100%{box-shadow:0 0 0 0 ${color}50} 50%{box-shadow:0 0 0 ${size*0.5}px ${color}00} }
      `}</style>
            <div style={{
        width: size, height: size, borderRadius: "50%",
        background: `radial-gradient(circle at 35% 35%, ${color}cc, ${color}66)`,
        border: `2px solid ${color}`,
        animation: anim,
        boxShadow: `0 0 20px ${color}40`,
      }} />
            <div style={{
        position: "absolute", inset: 0, borderRadius: "50%",
        background: "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.15), transparent 60%)",
      }} />
        </div>
    );
}

// ─── MARKET PRICE BAR ─────────────────────────────────────────────────────────
// Simplified from MarketWorldModel — removes DRL/DT/consensus/timingSync rows.
// Keeps: price dot with P&L color + trail + entry/target/stop zones + regime chips.
function MarketPriceBar({ ctx, pnl = null, panelState = "watching", regimeCtx = null, isCall = true }) {
    const { support, resistance, currentPrice, entryPrice, targetPrice, stopPrice, flipRate } = ctx;

    const hasPriceLevels = support != null && resistance != null && support !== resistance;

    // Always build a valid range — fall back to currentPrice ± 0.5% so dot
    // moves with live price even before S/R resolves from bar data.
    const cp = currentPrice || support || 580;
    const fallbackPad = cp * 0.005;

    const allPrices = [support, resistance, stopPrice, entryPrice, targetPrice, currentPrice]
        .filter((v) => v != null && v > 0);
    const barMin = allPrices.length > 1 ? Math.min(...allPrices) : cp - fallbackPad;
    const barMax = allPrices.length > 1 ? Math.max(...allPrices) : cp + fallbackPad;
    const padding = (barMax - barMin) * 0.05 || fallbackPad;
    const rangeMin = barMin - padding;
    const rangeMax = barMax + padding;
    const range    = rangeMax - rangeMin || 1;

    const pos = (v) => v != null ? Math.min(1, Math.max(0, (v - rangeMin) / range)) : null;

    // pricePos always computed — dot moves on every tradeEvent tick regardless of S/R
    const pricePos      = pos(currentPrice || cp);
    const entryPos      = entryPrice  ? pos(entryPrice)  : null;
    const targetPos     = targetPrice ? pos(targetPrice) : null;
    const stopPos       = stopPrice   ? pos(stopPrice)   : null;
    const supportPos    = support     ? pos(support)     : null;
    const resistancePos = resistance  ? pos(resistance)  : null;

    const isActive = panelState === "active";
    const dotColor = !isActive || pnl == null
        ? C.cyan
        : pnl >  0.02 ? C.green
        : pnl < -0.02 ? C.red
        : C.cyan;

    // Trail
    const trailRef   = useRef([]);
    const lastPosRef = useRef(pricePos);
    if (isActive && Math.abs(pricePos - lastPosRef.current) > 0.005) {
        trailRef.current = [lastPosRef.current, ...trailRef.current].slice(0, 3);
        lastPosRef.current = pricePos;
    }
    if (!isActive) { trailRef.current = []; lastPosRef.current = pricePos; }

    const flipColor = (flipRate ?? 0) < 0.15 ? C.green : (flipRate ?? 0) < 0.30 ? C.amber : C.red;

    // ── Direction-aware labels and zones ──────────────────────────────────────
    // LONG/CALL: target is ABOVE entry (right side), stop is BELOW (left side)
    // SHORT/PUT: target is BELOW entry (left side), stop is ABOVE (right side)
    const leftLabel  = isCall ? stopPrice   : targetPrice;
    const rightLabel = isCall ? targetPrice : stopPrice;
    const leftColor  = isCall ? C.red       : C.green;
    const rightColor = isCall ? C.green     : C.red;
    const leftTag    = isCall ? "STOP"      : "TARGET";
    const rightTag   = isCall ? "TARGET"    : "STOP";

    // Danger zone spans stop→entry regardless of direction
    const dangerLeft  = stopPos  !== null && entryPos !== null ? Math.min(stopPos, entryPos)   : null;
    const dangerWidth = stopPos  !== null && entryPos !== null ? Math.abs(entryPos - stopPos)  : 0;
    // Profit zone spans entry→target regardless of direction
    const profitLeft  = entryPos !== null && targetPos !== null ? Math.min(entryPos, targetPos) : null;
    const profitWidth = entryPos !== null && targetPos !== null ? Math.abs(targetPos - entryPos): 0;

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {/* Price bar */}
            <div>
                {/* Direction-aware label row */}
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                    <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                        {leftLabel
                            ? <span style={{ fontSize: "9px", color: leftColor, fontFamily: "'DM Mono', monospace", fontWeight: 600 }}>{leftTag} {fmt2(leftLabel)}</span>
                            : support
                            ? <span style={{ fontSize: "9px", color: C.muted, fontFamily: "'DM Mono', monospace" }}>S {fmt2(support)}</span>
                            : <span style={{ fontSize: "9px", color: C.muted, fontFamily: "'DM Mono', monospace" }}>Waiting for levels…</span>
                            }
                    </div>
                    <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                        {entryPrice && <span style={{ fontSize: "9px", color: C.amber, fontFamily: "'DM Mono', monospace" }}>ENTRY {fmt2(entryPrice)}</span>}
                        {rightLabel
                            ? <span style={{ fontSize: "9px", color: rightColor, fontFamily: "'DM Mono', monospace", fontWeight: 600 }}>{rightTag} {fmt2(rightLabel)}</span>
                            : resistance && <span style={{ fontSize: "9px", color: C.muted, fontFamily: "'DM Mono', monospace" }}>R {fmt2(resistance)}</span>
                            }
                    </div>
                </div>

                <div style={{ position: "relative", height: "36px", background: C.s3, borderRadius: "6px", overflow: "visible" }}>
                    {/* Danger zone — stop to entry */}
                    {dangerLeft !== null && dangerWidth > 0 && (
                    <div style={{ position: "absolute", left: `${dangerLeft * 100}%`, width: `${dangerWidth * 100}%`, top: 0, bottom: 0, background: `${C.red}18`, borderRadius: "2px" }} />
                        )}
                    {/* Profit zone — entry to target */}
                    {profitLeft !== null && profitWidth > 0 && (
                    <div style={{ position: "absolute", left: `${profitLeft * 100}%`, width: `${profitWidth * 100}%`, top: 0, bottom: 0, background: `${C.green}18`, borderRadius: "2px" }} />
                        )}

                    {/* Level lines */}
                    {(() => {
                        const levels = [
                            stopPos       != null && { pos: stopPos,       color: C.red,   label: "STOP",  solid: true  },
                            supportPos    != null && { pos: supportPos,    color: "#f59e0b",label: "S",     solid: false },
                            entryPos      != null && { pos: entryPos,      color: C.amber, label: "ENTRY", solid: true  },
                            resistancePos != null && { pos: resistancePos, color: "#60a5fa",label: "R",     solid: false },
                            targetPos     != null && { pos: targetPos,     color: C.green, label: "TP",    solid: true  },
                            ].filter(Boolean).sort((a, b) => a.pos - b.pos);

                        return levels.map((lv, i) => {
                            const above = i % 2 === 0;
                            return (
                            <div key={lv.label} style={{ position: "absolute", left: `${lv.pos * 100}%`, top: 0, bottom: 0, zIndex: 1 }}>
                                <div style={{ width: lv.solid ? "2px" : "1px", height: "100%", background: lv.solid ? lv.color : `${lv.color}80`, margin: "0 auto", borderRadius: "1px" }} />
                                <div style={{
                    position: "absolute", left: "50%", transform: "translateX(-50%)",
                    ...(above ? { bottom: "calc(100% + 2px)" } : { top: "calc(100% + 2px)" }),
                    fontSize: "7px", color: lv.color, fontFamily: "monospace", whiteSpace: "nowrap", fontWeight: lv.solid ? 700 : 400,
                  }}>{lv.label}</div>
                            </div>
                                );
                            });
                        })()}

                    {/* Trail dots */}
                    {trailRef.current.map((tp, i) => (
                    <div key={i} className="fbp-trail" style={{
              position: "absolute", left: `${tp * 100}%`, top: "50%",
              transform: "translate(-50%,-50%)",
              width: `${8 - i * 2}px`, height: `${8 - i * 2}px`,
              borderRadius: "50%", background: dotColor,
              opacity: 0.35 - i * 0.1, zIndex: 2, pointerEvents: "none",
            }} />
                        ))}

                    {/* Ripple */}
                    {isActive && (
                    <div style={{
              position: "absolute", left: `${pricePos * 100}%`, top: "50%",
              width: "14px", height: "14px", borderRadius: "50%",
              border: `2px solid ${dotColor}`,
              animation: "fbpRipple 1.4s ease-out infinite",
              zIndex: 1, pointerEvents: "none",
            }} />
                        )}

                    {/* Price dot */}
                    <div style={{
            position: "absolute", left: `${pricePos * 100}%`, top: "50%",
            transform: "translate(-50%, -50%)",
            width: "14px", height: "14px", borderRadius: "50%",
            background: dotColor, boxShadow: `0 0 ${isActive ? 10 : 6}px ${dotColor}${isActive ? "cc" : "80"}`,
            zIndex: 3,
          }}>
            <span style={{
              position: "absolute", bottom: "18px", left: "50%",
              transform: "translateX(-50%)",
              fontSize: "9px", color: dotColor, fontFamily: "monospace",
              whiteSpace: "nowrap", fontWeight: 700,
            }}>
              ${typeof currentPrice === "number" ? currentPrice.toFixed(2) : "—"}
            </span>
                    </div>
                </div>
            </div>

            {/* Regime chips row — replaces DRL/DT model row */}
            <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                {/* Day type */}
                {regimeCtx?.dayType && (
                    <Tooltip text="Day Type">
                    <span style={{
                    fontSize: "9px", padding: "2px 7px", cursor: "help",
                    background: dayTypeBg(regimeCtx.dayType),
                    color:      dayTypeColor(regimeCtx.dayType),
                    border:     `1px solid ${dayTypeColor(regimeCtx.dayType)}40`,
                    borderRadius: "3px", fontFamily: "monospace", fontWeight: 700,
                    }}>
                    {regimeCtx.dayType.replace(/_/g, " ")}
                    </span>
                    </Tooltip>
                    )}

                {/* Volatility context */}
                {regimeCtx?.volatilityCtx && (
                    <span style={{ fontSize: "9px", padding: "2px 7px", background: C.s3, color: C.body, border: `1px solid ${C.border}`, borderRadius: "3px", fontFamily: "monospace" }}>
                    {regimeCtx.volatilityCtx}
                    </span>
                    )}

                {/* Event day */}
                {regimeCtx?.eventDay && (
                    <span style={{ fontSize: "9px", padding: "2px 7px", background: C.cDim, color: C.cyan, border: `1px solid ${C.cyan}40`, borderRadius: "3px", fontFamily: "monospace", fontWeight: 700 }}>
                    EVENT DAY
                    </span>
                    )}

                {/* Flip rate */}
                {flipRate != null && (
                <Tooltip text="Flip Rate">
                    <div style={{ display: "flex", alignItems: "center", gap: "5px", cursor: "help" }}>
                        <span style={{ fontSize: "9px", color: C.muted, fontFamily: "monospace" }}>FLIP</span>
                        <div style={{ width: "46px", height: "5px", background: C.s3, borderRadius: "3px", overflow: "hidden" }}>
                            <div style={{ width: `${Math.min((flipRate || 0) * 100, 100)}%`, height: "100%", background: flipColor }} />
                        </div>
              <span style={{ fontSize: "9px", color: flipColor, fontFamily: "monospace" }}>
                {typeof flipRate === "number" ? pct(flipRate) : "—"}
              </span>
                    </div>
                </Tooltip>
                    )}

                {/* Stability score */}
                {regimeCtx?.stabilityScore != null && (
                    <Tooltip text="Stability Score">
                    <div style={{ display: "flex", alignItems: "center", gap: "5px", cursor: "help" }}>
                    <span style={{ fontSize: "9px", color: C.muted, fontFamily: "monospace" }}>STAB</span>
                    <div style={{ width: "40px", height: "5px", background: C.s3, borderRadius: "3px", overflow: "hidden" }}>
                    <div style={{ width: `${(regimeCtx.stabilityScore || 0) * 100}%`, height: "100%", background: stabilityColor(regimeCtx.stabilityScore) }} />
                    </div>
                    <span style={{ fontSize: "9px", color: stabilityColor(regimeCtx.stabilityScore), fontFamily: "monospace" }}>
                    {Math.round(regimeCtx.stabilityScore * 100)}
                    </span>
                    </div>
                    </Tooltip>
                    )}

                {/* Chop index */}
                {regimeCtx?.ci14 != null && (
                    <Tooltip text="Chop Index">
                    <span style={{
                    fontSize: "9px", padding: "2px 7px", cursor: "help", background: C.s3,
                    color: regimeCtx.ci14 < 38.2 ? C.green : regimeCtx.ci14 > 61.8 ? C.red : C.amber,
                    border: `1px solid ${C.border}`, borderRadius: "3px", fontFamily: "monospace",
                    }}>
                    CI {regimeCtx.ci14.toFixed(1)}
                    </span>
                    </Tooltip>
                    )}

                {/* DER */}
                {regimeCtx?.der != null && (
                    <Tooltip text="DER">
                    <span style={{
                    fontSize: "9px", padding: "2px 7px", cursor: "help", background: C.s3,
                    color: regimeCtx.der >= 0.7 ? C.green : regimeCtx.der >= 0.4 ? C.amber : C.red,
                    border: `1px solid ${C.border}`, borderRadius: "3px", fontFamily: "monospace",
                    }}>
                    DER {regimeCtx.der.toFixed(2)}
                    </span>
                    </Tooltip>
                    )}
            </div>
        </div>
    );
}

// ─── FOLLOWER POSITION BLOCK ──────────────────────────────────────────────────
// Replaces StockOptionsLink. Shows follower's option position + master strategy.
// No "shares" / no LONG/SHORT — options only.
function FollowerPositionBlock({ position, optionStrategy }) {
    if (!position) return null;

    // Derive direction from master_position_id until backend sends option_type
    // Format: "SPY-SHORT-YYYYMMDD-HHMMSS" or "SPY-LONG-..."
    const posStr  = position?.master_position_id || "";
    const isCall  = posStr.includes("LONG");
    const pnl     = position.realized_pnl ?? null;
    const isClosed = !!position.exit_fill_price;
    const pnlColor = pnl == null ? C.muted : pnl >= 0 ? C.green : C.red;
    const pnlFmt   = pnl == null ? "--" : `${pnl >= 0 ? "+" : ""}$${Math.abs(pnl).toFixed(2)}`;

    return (
        <div style={{ display: "flex", alignItems: "stretch", background: C.s2, borderRadius: "8px", border: `1px solid ${C.border}`, overflow: "hidden" }}>
            {/* Left: follower position */}
            <div style={{ flex: 1, padding: "10px 14px" }}>
                <div style={{ fontSize: "9px", color: C.muted, fontFamily: "'DM Mono', monospace", marginBottom: "5px", letterSpacing: "1px" }}>YOUR POSITION</div>
                <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px" }}>
          <span style={{
            fontSize: "9px", padding: "1px 6px",
            background: isCall ? C.gDim : C.rDim,
            color: isCall ? C.green : C.red,
            borderRadius: "3px", fontFamily: "monospace", fontWeight: 700,
          }}>{isCall ? "CALL" : "PUT"}</span>
          <span style={{ fontSize: "11px", fontWeight: 700, color: C.text, fontFamily: "'DM Mono', monospace" }}>
            {position.contract_symbol || "—"}
          </span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px" }}>
                    {[
                        ["Qty",   position.qty ?? "--"],
                        ["Entry", position.entry_fill_price != null ? `$${Number(position.entry_fill_price).toFixed(2)}` : "--"],
                        ["Exit",  isClosed && position.exit_fill_price != null ? `$${Number(position.exit_fill_price).toFixed(2)}` : "--"],
                        ["P&L",   pnlFmt],
                        ].map(([l, v]) => (
                    <div key={l}>
                        <span style={{ fontSize: "8px", color: C.muted, fontFamily: "monospace" }}>{l} </span>
                        <span style={{ fontSize: "10px", fontWeight: 700, color: l === "P&L" ? pnlColor : C.text, fontFamily: "monospace" }}>{v}</span>
                    </div>
                        ))}
                </div>
            </div>

            {/* Arrow */}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "0 10px", gap: "4px", borderLeft: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`, background: C.s3 }}>
                <span style={{ fontSize: "16px", color: C.cyan }}>→</span>
                <span style={{ fontSize: "8px", color: C.muted, fontFamily: "monospace", textAlign: "center", maxWidth: "40px", lineHeight: 1.3 }}>driven by</span>
            </div>

            {/* Right: master strategy (read-only) */}
            <div style={{ flex: 1, padding: "10px 14px" }}>
                <div style={{ fontSize: "9px", color: C.muted, fontFamily: "'DM Mono', monospace", marginBottom: "5px", letterSpacing: "1px" }}>MASTER STRATEGY</div>
                {optionStrategy ? (
                <>
                <div style={{ fontSize: "12px", fontWeight: 700, color: C.cyan, fontFamily: "'Syne', sans-serif", marginBottom: "4px" }}>
                    {optionStrategy.replace(/_/g, " ")}
                </div>
                <div style={{ fontSize: "9px", color: C.muted, fontFamily: "monospace", lineHeight: 1.4 }}>
                    Closes when SPY hits target or stop
                </div>
                </>
                    ) : (
                <span style={{ fontSize: "9px", color: C.subtle, fontFamily: "monospace" }}>Strategy not available</span>
                    )}
            </div>
        </div>
    );
}

// ─── TREND STRIP ──────────────────────────────────────────────────────────────
// Identical to master — last 6 reactive eval checks
function TrendStrip({ checks }) {
    if (!checks || checks.length === 0) return null;
    return (
        <div style={{ display: "flex", alignItems: "center", gap: "6px", padding: "8px 12px", background: C.s3, borderRadius: "6px" }}>
            <span style={{ fontSize: "9px", color: C.muted, fontFamily: "monospace", marginRight: "4px" }}>EVAL HISTORY</span>
            {checks.map((c, i) => {
                const bucket = (c.conf || 0) > 0.70 ? "HIGH" : (c.conf || 0) > 0.50 ? "MEDIUM" : "LOW";
                return (
                <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "3px" }}>
                    <div style={{
              width: "28px", height: "28px", borderRadius: "50%",
              background: confColor(bucket) + "20",
              border: `2px solid ${confColor(bucket)}`,
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "1px",
            }}>
              <span style={{ fontSize: "8px", fontWeight: 700, color: confColor(bucket), fontFamily: "monospace", lineHeight: 1 }}>
                {Math.round((c.conf || 0) * 100)}
              </span>
                        {c.flags > 0 && <span style={{ fontSize: "7px", color: C.red, lineHeight: 1 }}>⚑{c.flags}</span>}
                    </div>
                    <span style={{ fontSize: "8px", color: C.muted, fontFamily: "monospace" }}>{c.time}</span>
                    <span style={{ fontSize: "7px", color: C.subtle, fontFamily: "monospace" }}>{c.decision}</span>
                </div>
                    );
                })}
            <div style={{ flex: 1, height: "1px", background: `linear-gradient(90deg, ${C.border}, transparent)`, marginLeft: "4px" }} />
            <span style={{ fontSize: "9px", color: C.muted, fontFamily: "monospace" }}>next: 5m</span>
        </div>
    );
}

// ─── DATA HOOK ────────────────────────────────────────────────────────────────
// Replaces usePanelData from master — reads from flat followerBars instead of
// drilling into eval_agent_story blobs.
function usePanelData({ followerBars, openPositions, closedPositions, currentPrice, metrics }) {
    return useMemo(() => {
        const activePos  = openPositions?.[0] || null;
        const lastClosed = closedPositions?.length > 0
            ? closedPositions[closedPositions.length - 1] : null;

        // Panel state
        let panelState = "watching";
        if (activePos) panelState = "active";
        else if (lastClosed && lastClosed.closed_at) {
            const closedAgo = (Date.now() - new Date(lastClosed.closed_at)) / 60000;
            if (closedAgo < 30) panelState = "post_trade";
        }

        // Today stats — scan followerBars for eval decisions
        const today = new Date().toDateString();
        let evaluated = 0, proceeded = 0, skipped = 0;
        (followerBars || []).forEach((bar) => {
            const barDate = bar.t ? new Date(bar.t).toDateString() : "";
            if (barDate !== today) return;
            const dec = bar.eval_decision;
            if (!dec) return;
            evaluated++;
            if (dec === "PROCEED" || dec === "BUY") proceeded++;
            else if (dec === "SKIP_TRADE" || dec === "SKIP") skipped++;
        });
        const todayStats = { evaluated, proceeded, skipped };

        // Find best eval bar for agent story:
        // Active: prefer reactive (eval_position_id matches), then last PROCEED
        // Watching/post_trade: last PROCEED bar
        const positionId = activePos?.master_position_id || activePos?.id;

        const reactiveBars = positionId
            ? (followerBars || []).filter(b =>
        (b.eval_position_id || b.position_id) === positionId)
            : [];
        const lastReactiveBar = reactiveBars.length > 0
            ? reactiveBars[reactiveBars.length - 1] : null;

        const lastProceedBar = (followerBars || []).slice().reverse().find(b =>
                (b.eval_decision === "PROCEED" || b.eval_decision === "BUY") && b.eval_confidence != null
            ) || null;

        // Most recent bar with any eval (for watching state S/R + regime)
        const lastEvalBar = (followerBars || []).slice().reverse().find(b => b.eval_decision) || null;

        const agentBar = panelState === "active"
            ? (lastReactiveBar || lastProceedBar || lastEvalBar)
            : (lastProceedBar || lastEvalBar);

        const agentStory = extractFlatEval(agentBar);
        const proactiveStory = extractFlatEval(lastEvalBar);

        // Reactive history strip (last 6 position-specific eval bars)
        const reactiveHistory = reactiveBars.slice(-6).map((b) => ({
            time:     b.t ? new Date(b.t).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false }) : "—",
            conf:     b.eval_confidence ?? 0,
            ev:       b.eval_expected_value ?? null,
            flags:    b.eval_risk_flag_count || 0,
            decision: b.eval_decision || "—",
        }));

        // Market context — S/R from proactive or latest eval bar
        const ctxSrc = agentStory || proactiveStory;

        // ── Direction: derive from master_position_id until backend sends option_type ──
        // master_position_id format: "SPY-SHORT-YYYYMMDD-HHMMSS" or "SPY-LONG-..."
        // SHORT → PUT (price falling) · LONG → CALL (price rising)
        const posIdStr = activePos?.master_position_id || "";
        const isCall   = posIdStr.includes("LONG");  // false for SHORT, false when no pos

        // ── Position levels from servantPositionEvent ──────────────────────────────
        // Confirmed payload fields (from live event 2026-04-27):
        //   entry_fill_price  — option fill (e.g. 1.26)
        //   underlying_entry  — SPY price at entry (e.g. 713.51)
        //   underlying_target — SPY target price   (e.g. 713.38 for SHORT)
        //   underlying_stop   — SPY stop price     (e.g. 715.74 for SHORT)
        // All four are SPY price levels — use underlying_* for the price bar.
        const resolvedEntry  = panelState === "active"
            ? Number(activePos?.underlying_entry)  || null : null;
        const resolvedTarget = panelState === "active"
            ? Number(activePos?.underlying_target) || null : null;
        const resolvedStop   = panelState === "active"
            ? Number(activePos?.underlying_stop)   || null : null;
        // Option fill price — shown in position block, not on price bar
        const optionFill     = panelState === "active"
            ? Number(activePos?.entry_fill_price)  || null : null;

        const marketContext = {
            support:        ctxSrc?.supportLevel    ?? proactiveStory?.supportLevel    ?? null,
            resistance:     ctxSrc?.resistanceLevel ?? proactiveStory?.resistanceLevel ?? null,
            currentPrice,
            flipRate:       ctxSrc?.regimeCtx?.flipRate ?? null,
            entryPrice:     resolvedEntry,
            targetPrice:    resolvedTarget,
            stopPrice:      resolvedStop,
            isCall,
    };

        // Follower position block
        let position = null;
        if (activePos && panelState === "active") {
            position = activePos;
        } else if (lastClosed && panelState === "post_trade") {
            position = lastClosed;
        }

        // Eval object for views
        const ev = agentStory ? {
            decision:      agentStory.decision,
            confidence:    agentStory.confidence,
            bucket:        agentStory.confBucket,
            path:          agentStory.path,
            evalType:      agentStory.evalType,
            timeframeLock: agentStory.timeframeLock,
            memory:        agentStory.memory,
            performance:   agentStory.performance,
            riskFlagCount: agentStory.riskFlagCount,
            patternName:   agentStory.patternName,
            patternConf:   agentStory.patternConf,
            patternRel:    agentStory.patternRel,
            optionStrategy: agentStory.optionStrategy,
            narrative:     agentStory.narrative,
            mindChanged:   agentStory.mindChanged,
            regimeCtx:     agentStory.regimeCtx,
            timestamp:     agentStory.timestamp,
            flags:         agentStory.flags,
        } : null;

        // Orb
        let orbColor = C.blue;
        let orbPulse = "slow";
        if (panelState === "active") {
            const evNeg = ev?.performance?.ev != null && ev.performance.ev < 0;
            const notSupported = ev?.performance?.supports === false;
            orbColor = (evNeg || notSupported) ? C.amber : C.green;
            orbPulse = "medium";
        } else if (panelState === "post_trade") {
            const pnl = lastClosed?.realized_pnl ?? 0;
            orbColor = pnl >= 0 ? C.green : C.red;
            orbPulse = "none";
        }

        // Headline
        let headline    = "Bot is scanning for setups";
        let subheadline = `${evaluated} evaluated · ${proceeded} proceeded · ${skipped} skipped`;
        let tone        = "quiet";

        if (panelState === "watching") {
            const dayType       = agentStory?.regimeCtx?.dayType || null;
            const entryBlocked  = agentStory?.regimeCtx?.entryAllowed === false;
            const blockReason   = agentStory?.regimeCtx?.entryBlockReason || null;
            const flipR         = agentStory?.regimeCtx?.flipRate ?? 0;

            if (entryBlocked) {
                headline = blockReason
                    ? `Entry blocked — ${blockReason.toLowerCase()}`
                    : "Entry blocked — regime conditions below threshold";
                tone = "quiet";
            } else if (dayType === "CHOPPY_DAY" || dayType === "VOLATILE_DAY") {
                headline = "Selective day — bot waiting for cleaner conditions";
                tone     = "quiet";
            } else if (dayType === "EVENT_DAY" || dayType === "EVENT_TREND") {
                headline = "Event day detected — bot running with heightened caution";
                tone     = "confident";
            } else if (dayType === "TREND_DAY" || dayType === "STRONG_TREND_DAY") {
                headline = "Trending conditions — agent actively seeking setups";
                tone     = "confident";
            } else if (flipR > 0.30) {
                headline = "Selective day — bot waiting for cleaner conditions";
                tone     = "quiet";
            } else if (evaluated === 0) {
                headline = "Market conditions below threshold — waiting for qualifying signal";
                tone     = "quiet";
            } else {
                headline = "Scanning — conditions evaluated, waiting for high-quality entry";
                tone     = "quiet";
            }

        } else if (panelState === "active") {
            const evVal     = ev?.performance?.ev ?? null;
            const supported = ev?.performance?.supports ?? true;
            const confScore = ev?.confidence ?? 0;
            const locked    = ev?.timeframeLock?.active;
            const memCases  = ev?.memory?.cases ?? 0;
            const memRate   = ev?.memory?.successRate ?? null;

            if (evVal != null && evVal < 0 && !supported) {
                headline    = "Holding by discipline — conditions mixed but rules say stay";
                tone        = "disciplined";
                subheadline = locked
                    ? "Timeframe lock active · EV slightly negative · holding by rules"
                    : memCases > 0 && memRate != null
                    ? `${memCases} similar cases · ${Math.round(memRate * 100)}% won · history mixed`
                    : subheadline;
            } else if (confScore >= 0.70 && supported) {
                headline = "Holding as planned — conditions support continuation";
                tone     = "confident";
                if (memCases > 0 && memRate != null) {
                    subheadline = `${memCases} similar cases · ${Math.round(memRate * 100)}% won`;
                }
            } else if (locked) {
                headline    = "Timeframe lock active — letting the trade develop";
                tone        = "disciplined";
                subheadline = `Lock expires in ${ev.timeframeLock.remainingMin}m · next check on unlock`;
            } else {
                headline = "Monitoring position — bot running analysis every 5 minutes";
                tone     = "disciplined";
            }

        } else if (panelState === "post_trade") {
            const pnl     = lastClosed?.realized_pnl ?? 0;
            const isWin   = pnl >= 0;
            const holdMin = lastClosed?.open_at && lastClosed?.closed_at
                ? Math.floor((new Date(lastClosed.closed_at) - new Date(lastClosed.open_at)) / 60000) : "--";

            if (isWin) {
                headline    = "Target reached — trade closed as planned";
                subheadline = `Hold time: ${holdMin}m · P&L +$${Math.abs(pnl).toFixed(2)} · Resuming scan`;
                tone        = "confident";
            } else {
                headline    = "Stop triggered — loss contained, bot resuming scan";
                subheadline = `Hold time: ${holdMin}m · P&L -$${Math.abs(pnl).toFixed(2)}`;
                tone        = "quiet";
            }
        }

        return {
            panelState, orbColor, orbPulse, tone,
            headline, subheadline,
            todayStats, marketContext,
            position, ev, reactiveHistory,
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [followerBars, openPositions, closedPositions, currentPrice]);
}

// ─── DASHBOARD VIEW ───────────────────────────────────────────────────────────
function DashboardView({ data }) {
    const { orbColor, orbPulse, headline, subheadline, todayStats,
        marketContext, position, ev, reactiveHistory, panelState } = data;

    const [lockSecs, setLockSecs] = useState(
        ev?.timeframeLock?.active && ev.timeframeLock.remainingMin
        ? ev.timeframeLock.remainingMin * 60 : 0
);
    const prevLockActive = useRef(ev?.timeframeLock?.active);
    const lockActive = panelState === "active" && !!ev?.timeframeLock?.active;

    useEffect(() => {
        if (lockActive && !prevLockActive.current) {
            setLockSecs((ev.timeframeLock.remainingMin || 0) * 60);
        }
        if (!lockActive) setLockSecs(0);
        prevLockActive.current = lockActive;
    }, [lockActive, ev?.timeframeLock?.remainingMin]);

    useEffect(() => {
        if (!lockActive) return;
        const t = setInterval(() => setLockSecs((s) => Math.max(0, s - 1)), 1000);
        return () => clearInterval(t);
    }, [lockActive]);

    const lockMins  = Math.floor(lockSecs / 60);
    const lockSecs2 = lockSecs % 60;
    const evNeg     = ev?.performance?.ev != null && ev.performance.ev < 0;

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>

            {/* Row 1: Orb + Headline + Today stats */}
            <div style={{
        display: "flex", alignItems: "center", gap: "16px",
        padding: "16px 20px", background: C.s2, borderRadius: "10px",
        border: `1px solid ${C.border}`, borderLeft: `3px solid ${orbColor}`,
      }}>
                <StatusOrb color={orbColor} pulse={orbPulse} size={52} />
                <div style={{ flex: 1 }}>
                    <div style={{ fontSize: "17px", fontWeight: 700, color: C.text, fontFamily: "'Syne', sans-serif", lineHeight: 1.3, marginBottom: "5px" }}>
                        {headline}
                    </div>
                    <div style={{ fontSize: "12px", color: C.muted, fontFamily: "'DM Mono', monospace" }}>
                        {subheadline}
                    </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px", alignItems: "flex-end" }}>
                    <div style={{ display: "flex", gap: "6px" }}>
                        {[
                            ["evaluated", todayStats.evaluated, C.muted],
                            ["proceeded", todayStats.proceeded, C.green],
                            ["skipped",   todayStats.skipped,   C.subtle],
                            ].map(([l, v, col]) => (
                        <div key={l} style={{ padding: "3px 8px", background: C.s3, borderRadius: "4px", border: `1px solid ${C.border}` }}>
                            <span style={{ fontSize: "11px", fontWeight: 700, color: col, fontFamily: "monospace" }}>{v}</span>
                            <span style={{ fontSize: "9px",  color: C.muted, fontFamily: "monospace" }}> {l}</span>
                        </div>
                            ))}
                    </div>
                </div>
            </div>

            {/* Row 2: Market context + Key metrics */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "14px" }}>
                <div style={{ padding: "14px 16px", background: C.s2, borderRadius: "10px", border: `1px solid ${C.border}` }}>
                    <div style={{ fontSize: "9px", color: C.muted, fontFamily: "monospace", letterSpacing: "1px", marginBottom: "12px" }}>MARKET CONTEXT</div>
                    <MarketPriceBar
                        ctx={marketContext}
                        pnl={position?.realized_pnl ?? null}
                        panelState={panelState}
                        regimeCtx={ev?.regimeCtx ?? null}
                        isCall={marketContext.isCall}
                    />
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "8px", minWidth: "130px" }}>
                    {/* Confidence */}
                    <div style={{ padding: "10px 14px", background: C.s2, borderRadius: "8px", border: `1px solid ${C.border}`, flex: 1 }}>
                        <div style={{ fontSize: "9px", color: C.muted, fontFamily: "monospace", marginBottom: "4px" }}>CONFIDENCE</div>
                        <div style={{ fontSize: "20px", fontWeight: 700, color: confColor(ev?.bucket), fontFamily: "'Syne', sans-serif", lineHeight: 1 }}>
                            {ev?.bucket || "—"}
                        </div>
                        <div style={{ fontSize: "10px", color: C.muted, fontFamily: "monospace", marginTop: "2px" }}>
                            {ev?.confidence != null ? `${Math.round(ev.confidence * 100)}%` : ""}
                        </div>
                    </div>

                    {/* EV */}
                    {ev?.performance?.ev != null && (
                        <Tooltip text="EV">
                        <div style={{
                        padding: "10px 14px", background: C.s2, borderRadius: "8px", flex: 1,
                        border: `1px solid ${evNeg ? C.amber + "40" : C.border}`, cursor: "help",
                        }}>
                        <div style={{ fontSize: "9px", color: C.muted, fontFamily: "monospace", marginBottom: "4px" }}>EXP. VALUE</div>
                        <div style={{ fontSize: "16px", fontWeight: 700, color: evNeg ? C.amber : C.green, fontFamily: "'Syne', sans-serif", lineHeight: 1 }}>
                        {evNeg ? "-" : "+"}${Math.abs(ev.performance.ev).toFixed(2)}
                        </div>
                        {!ev.performance.supports && (
                        <div style={{ fontSize: "8px", color: C.amber, fontFamily: "monospace", marginTop: "3px" }}>history mixed</div>
                            )}
                        </div>
                        </Tooltip>
                        )}

                    {/* Timeframe lock */}
                    {lockActive && (
                    <Tooltip text="Timeframe Lock">
                        <div style={{ padding: "10px 14px", background: C.aDim, borderRadius: "8px", border: `1px solid ${C.amber}40`, flex: 1, cursor: "help" }}>
                            <div style={{ fontSize: "9px", color: C.amber, fontFamily: "monospace", marginBottom: "4px" }}>LOCK EXPIRES</div>
                            <div style={{ fontSize: "18px", fontWeight: 700, color: C.amber, fontFamily: "'Syne', sans-serif", lineHeight: 1 }}>
                                {lockMins}:{lockSecs2.toString().padStart(2, "0")}
                            </div>
                        </div>
                    </Tooltip>
                        )}

                    {/* Risk flags */}
                    {ev?.riskFlagCount != null && (
                        <div style={{
                        padding: "10px 14px", background: C.s2, borderRadius: "8px", flex: 1,
                        border: `1px solid ${ev.riskFlagCount >= 4 ? C.red + "40" : C.border}`,
                        }}>
                        <div style={{ fontSize: "9px", color: C.muted, fontFamily: "monospace", marginBottom: "4px" }}>FLAGS</div>
                        <div style={{ fontSize: "20px", fontWeight: 700, color: ev.riskFlagCount >= 4 ? C.red : ev.riskFlagCount >= 2 ? C.amber : C.muted, fontFamily: "'Syne', sans-serif", lineHeight: 1 }}>
                        {ev.riskFlagCount}/5
                        </div>
                        </div>
                        )}
                </div>
            </div>

            {/* Row 2.5: Entry status banner */}
            {ev?.regimeCtx && (ev.regimeCtx.entryAllowed === false || ev.regimeCtx.cooldownActive) && panelState === "watching" && (
                <div style={{
                padding: "10px 14px", borderRadius: "8px",
                background: ev.regimeCtx.entryAllowed === false ? C.aDim : C.s2,
                border: `1px solid ${ev.regimeCtx.entryAllowed === false ? C.amber + "50" : C.border}`,
                borderLeft: `3px solid ${ev.regimeCtx.entryAllowed === false ? C.amber : C.muted}`,
                display: "flex", alignItems: "flex-start", gap: "12px", flexWrap: "wrap",
                }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontSize: "9px", color: C.muted, fontFamily: "monospace", letterSpacing: "1px", marginBottom: "5px" }}>BOT ENTRY STATUS</div>
                {ev.regimeCtx.entryAllowed === false ? (
                <div style={{ fontSize: "11px", color: C.amber, fontFamily: "'DM Mono', monospace", lineHeight: 1.5 }}>
                    {ev.regimeCtx.entryBlockReason
                        ? `Entry blocked — ${ev.regimeCtx.entryBlockReason}`
                        : "Entry blocked — regime conditions do not meet entry criteria"}
                </div>
                    ) : (
                <div style={{ fontSize: "11px", color: C.body, fontFamily: "'DM Mono', monospace", lineHeight: 1.5 }}>
                    Entry allowed — waiting for qualifying signal
                </div>
                    )}
                </div>
                <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", alignItems: "center" }}>
                {ev.regimeCtx.entryAllowed === false && (
                <span style={{ fontSize: "9px", padding: "2px 8px", background: C.rDim, color: C.red, border: `1px solid ${C.red}40`, borderRadius: "3px", fontFamily: "monospace", fontWeight: 700 }}>ENTRY BLOCKED</span>
                    )}
                {ev.regimeCtx.cooldownActive && (
                <span style={{ fontSize: "9px", padding: "2px 8px", background: C.rDim, color: C.red, border: `1px solid ${C.red}40`, borderRadius: "3px", fontFamily: "monospace", fontWeight: 700 }}>COOLDOWN</span>
                    )}
                </div>
                </div>
                )}

            {/* Row 3: Follower position block */}
            {position && (
            <FollowerPositionBlock
                position={position}
                optionStrategy={ev?.optionStrategy}
            />
                )}

            {/* Row 4: Reactive history */}
            {reactiveHistory.length > 0 && <TrendStrip checks={reactiveHistory} />}
        </div>
    );
}

// ─── ANALYSIS VIEW ────────────────────────────────────────────────────────────
// Stripped of thinking arc, rule votes, evidence weights, model consensus.
// Keeps: execution path, decision/confidence, regime params, memory, pattern, narrative.
function AnalysisView({ data }) {
    const { ev, panelState } = data;

    const [lockSecs, setLockSecs] = useState(
        ev?.timeframeLock?.active && ev.timeframeLock.remainingMin
        ? ev.timeframeLock.remainingMin * 60 : 0
);

    useEffect(() => {
        if (!ev?.timeframeLock?.active) return;
        const t = setInterval(() => setLockSecs((s) => Math.max(0, s - 1)), 1000);
        return () => clearInterval(t);
    }, [ev?.timeframeLock?.active]);

    const lockMins  = Math.floor(lockSecs / 60);
    const lockSecs2 = lockSecs % 60;

    const Card = ({ title, accent, children }) => (
        <div style={{ background: C.s2, borderRadius: "8px", border: `1px solid ${C.border}`, overflow: "hidden" }}>
            <div style={{
        padding: "8px 12px", background: C.s3,
        borderBottom: `1px solid ${C.border}`, borderLeft: `3px solid ${accent || C.border}`,
      }}>
                <span style={{ fontSize: "9px", fontWeight: 700, color: accent || C.muted, fontFamily: "monospace", letterSpacing: "1px", textTransform: "uppercase" }}>{title}</span>
            </div>
            <div style={{ padding: "12px" }}>{children}</div>
        </div>
    );

    if (!ev) {
        return (
            <div style={{ padding: "24px", textAlign: "center", display: "flex", flexDirection: "column", gap: "8px", alignItems: "center" }}>
                <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: C.muted, opacity: 0.5 }} />
        <span style={{ fontSize: "11px", color: C.muted, fontFamily: "monospace" }}>
          Waiting for first agent evaluation...
        </span>
            </div>
        );
    }

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>

            {/* Context banner */}
            {panelState !== "active" && (
            <div style={{ padding: "7px 12px", borderRadius: "6px", background: C.s3, border: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: "8px" }}>
                <div style={{ width: "6px", height: "6px", borderRadius: "50%", background: panelState === "post_trade" ? C.amber : C.cyan, flexShrink: 0 }} />
          <span style={{ fontSize: "9px", color: C.muted, fontFamily: "monospace" }}>
            {panelState === "post_trade" ? "LAST REACTIVE EVAL — position just closed" : "LAST PROCEED EVAL — most recent entry decision"}
          </span>
                {ev.timestamp && (
                <span style={{ fontSize: "9px", color: C.subtle, fontFamily: "monospace", marginLeft: "auto" }}>
              {new Date(ev.timestamp).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false })}
            </span>
                    )}
            </div>
                )}

            {/* Header strip: path + decision + confidence */}
            <div style={{ display: "flex", gap: "8px", alignItems: "center", padding: "8px 14px", background: C.s3, borderRadius: "8px", flexWrap: "wrap" }}>
                {ev.path && (
                <>
                <span style={{ fontSize: "9px", color: C.muted, fontFamily: "monospace" }}>PATH</span>
            <span style={{
              fontSize: "10px", fontWeight: 700, fontFamily: "monospace",
              color:      ev.path === "fast_path" ? C.green : C.blue,
              padding:    "2px 8px",
              background: ev.path === "fast_path" ? C.gDim  : C.bDim,
              borderRadius: "3px",
            }}>
              {ev.path === "fast_path" ? "⚡ FAST PATH" : "🔄 FULL PATH"}
            </span>
                <div style={{ width: "1px", height: "16px", background: C.border }} />
                </>
                    )}
                <span style={{ fontSize: "9px", color: C.muted, fontFamily: "monospace" }}>DECISION</span>
                <span style={{ fontSize: "10px", fontWeight: 700, color: confColor(ev.bucket), fontFamily: "monospace" }}>{ev.decision}</span>
                <div style={{ width: "1px", height: "16px", background: C.border }} />
        <span style={{ fontSize: "10px", color: confColor(ev.bucket), fontFamily: "monospace" }}>
          {ev.bucket || "—"}{ev.confidence != null ? ` · ${Math.round(ev.confidence * 100)}%` : ""}
        </span>
                {ev.mindChanged && (
                <>
                <div style={{ width: "1px", height: "16px", background: C.border }} />
                <span style={{ fontSize: "9px", padding: "2px 7px", background: C.aDim, color: C.amber, borderRadius: "3px", fontFamily: "monospace", fontWeight: 700 }}>MIND CHANGED</span>
                </>
                    )}
                {ev.timeframeLock?.active && (
                    <div style={{ marginLeft: "auto", padding: "4px 10px", background: C.aDim, border: `1px solid ${C.amber}40`, borderRadius: "6px", display: "flex", gap: "8px", alignItems: "center" }}>
                    <span style={{ fontSize: "9px", color: C.amber, fontFamily: "monospace" }}>🔒 LOCK</span>
                    <span style={{ fontSize: "12px", fontWeight: 700, color: C.amber, fontFamily: "monospace" }}>
                    {lockMins}:{lockSecs2.toString().padStart(2, "0")}
                    </span>
                    </div>
                    )}
            </div>

            {/* Narrative summary */}
            {ev.narrative && (
            <Card title="Agent Reasoning" accent={C.cyan}>
                <div style={{ fontSize: "11px", color: C.body, fontFamily: "'DM Mono', monospace", lineHeight: 1.7 }}>
                    {ev.narrative}
                </div>
            </Card>
                )}

            {/* Regime context */}
            {ev.regimeCtx && (
            <Card title="Regime Context" accent={dayTypeColor(ev.regimeCtx.dayType)}>
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                        {ev.regimeCtx.dayType && (
                        <Tooltip text="Day Type">
                  <span style={{ fontSize: "9px", padding: "2px 8px", background: dayTypeBg(ev.regimeCtx.dayType), color: dayTypeColor(ev.regimeCtx.dayType), border: `1px solid ${dayTypeColor(ev.regimeCtx.dayType)}40`, borderRadius: "3px", fontFamily: "monospace", fontWeight: 700, cursor: "help" }}>
                    {ev.regimeCtx.dayType.replace(/_/g, " ")}
                  </span>
                        </Tooltip>
                            )}
                        {ev.regimeCtx.entryType && (
                        <span style={{ fontSize: "9px", padding: "2px 8px", background: C.cDim, color: C.cyan, border: `1px solid ${C.cyan}40`, borderRadius: "3px", fontFamily: "monospace" }}>
                  {ev.regimeCtx.entryType.replace(/_/g, " ")}
                </span>
                            )}
                        {ev.regimeCtx.eventDay && (
                        <span style={{ fontSize: "9px", padding: "2px 8px", background: C.cDim, color: C.cyan, border: `1px solid ${C.cyan}40`, borderRadius: "3px", fontFamily: "monospace", fontWeight: 700 }}>EVENT DAY</span>
                            )}
                        {ev.regimeCtx.volatilityCtx && (
                        <span style={{ fontSize: "9px", padding: "2px 8px", background: C.s3, color: C.body, border: `1px solid ${C.border}`, borderRadius: "3px", fontFamily: "monospace" }}>
                  {ev.regimeCtx.volatilityCtx}
                </span>
                            )}
                    </div>

                    {/* Numeric indicators */}
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
                        {ev.regimeCtx.rrFloor != null && (
                        <div>
                            <div style={{ fontSize: "8px", color: C.muted, fontFamily: "monospace", marginBottom: "2px" }}>R:R FLOOR</div>
                            <div style={{ fontSize: "13px", fontWeight: 700, color: C.text, fontFamily: "monospace" }}>{ev.regimeCtx.rrFloor.toFixed(2)}</div>
                        </div>
                            )}
                        {ev.regimeCtx.scaleCap != null && (
                        <Tooltip text="Risk Params">
                            <div style={{ cursor: "help" }}>
                                <div style={{ fontSize: "8px", color: C.muted, fontFamily: "monospace", marginBottom: "2px" }}>SCALE CAP</div>
                                <div style={{ fontSize: "13px", fontWeight: 700, color: C.text, fontFamily: "monospace" }}>{Math.round(ev.regimeCtx.scaleCap * 100)}%</div>
                            </div>
                        </Tooltip>
                            )}
                        {ev.regimeCtx.der != null && (
                        <Tooltip text="DER">
                            <div style={{ cursor: "help" }}>
                                <div style={{ fontSize: "8px", color: C.muted, fontFamily: "monospace", marginBottom: "2px" }}>DER</div>
                                <div style={{ fontSize: "13px", fontWeight: 700, color: ev.regimeCtx.der >= 0.7 ? C.green : ev.regimeCtx.der >= 0.4 ? C.amber : C.red, fontFamily: "monospace" }}>{ev.regimeCtx.der.toFixed(2)}</div>
                            </div>
                        </Tooltip>
                            )}
                        {ev.regimeCtx.ci14 != null && (
                        <Tooltip text="Chop Index">
                            <div style={{ cursor: "help" }}>
                                <div style={{ fontSize: "8px", color: C.muted, fontFamily: "monospace", marginBottom: "2px" }}>CI-14</div>
                                <div style={{ fontSize: "13px", fontWeight: 700, color: ev.regimeCtx.ci14 < 38.2 ? C.green : ev.regimeCtx.ci14 > 61.8 ? C.red : C.amber, fontFamily: "monospace" }}>{ev.regimeCtx.ci14.toFixed(1)}</div>
                            </div>
                        </Tooltip>
                            )}
                        {ev.regimeCtx.stabilityScore != null && (
                        <Tooltip text="Stability Score">
                            <div style={{ cursor: "help" }}>
                                <div style={{ fontSize: "8px", color: C.muted, fontFamily: "monospace", marginBottom: "2px" }}>STABILITY</div>
                                <div style={{ fontSize: "13px", fontWeight: 700, color: stabilityColor(ev.regimeCtx.stabilityScore), fontFamily: "monospace" }}>{Math.round(ev.regimeCtx.stabilityScore * 100)}</div>
                            </div>
                        </Tooltip>
                            )}
                    </div>
                </div>
            </Card>
                )}

            {/* Memory */}
            {ev.memory && (
            <Card title="Memory" accent={ev.memory.cases > 0 ? C.green : C.red}>
                <Tooltip text="Memory">
                    <div style={{ display: "flex", gap: "20px", alignItems: "flex-start", cursor: "help" }}>
                        <div>
                            <div style={{ fontSize: "8px", color: C.muted, fontFamily: "monospace", marginBottom: "3px" }}>CASES FOUND</div>
                            <div style={{ fontSize: "22px", fontWeight: 700, color: ev.memory.cases > 0 ? C.green : C.red, fontFamily: "'Syne', sans-serif" }}>{ev.memory.cases}</div>
                        </div>
                        {ev.memory.cases > 0 && (
                        <>
                        <div>
                            <div style={{ fontSize: "8px", color: C.muted, fontFamily: "monospace", marginBottom: "3px" }}>SIMILARITY</div>
                            <div style={{ fontSize: "22px", fontWeight: 700, color: C.cyan, fontFamily: "'Syne', sans-serif" }}>{Math.round((ev.memory.similarity || 0) * 100)}%</div>
                        </div>
                        {ev.memory.successRate != null && (
                        <div>
                            <div style={{ fontSize: "8px", color: C.muted, fontFamily: "monospace", marginBottom: "3px" }}>WIN RATE</div>
                            <div style={{ fontSize: "22px", fontWeight: 700, color: (ev.memory.successRate || 0) > 0.6 ? C.green : C.amber, fontFamily: "'Syne', sans-serif" }}>
                                {Math.round((ev.memory.successRate || 0) * 100)}%
                            </div>
                        </div>
                            )}
                        </>
                            )}
                        {ev.memory.cases === 0 && (
                        <div style={{ fontSize: "11px", color: C.muted, fontFamily: "monospace", paddingTop: "6px" }}>No similar historical setups found. Bot acting on model signals only.</div>
                            )}
                    </div>
                </Tooltip>
            </Card>
                )}

            {/* Pattern */}
            {ev.patternName && (
            <Card title="Pattern Detected" accent={C.purple}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <div style={{ flex: 1 }}>
                        <div style={{ fontSize: "12px", fontWeight: 700, color: C.text, fontFamily: "'DM Mono', monospace", marginBottom: "4px" }}>{ev.patternName}</div>
                        {ev.patternRel != null && (
                        <div style={{ fontSize: "9px", color: C.muted, fontFamily: "monospace" }}>
                            Historical reliability: <span style={{ color: ev.patternRel > 0.6 ? C.green : C.amber }}>{Math.round(ev.patternRel * 100)}%</span>
                        </div>
                            )}
                    </div>
                    {ev.patternConf != null && (
                    <div style={{ textAlign: "center" }}>
                        <div style={{ fontSize: "8px", color: C.muted, fontFamily: "monospace", marginBottom: "2px" }}>CONF</div>
                        <div style={{ fontSize: "20px", fontWeight: 700, color: confColor(bucketFromScore(ev.patternConf)), fontFamily: "'Syne', sans-serif" }}>
                            {Math.round(ev.patternConf * 100)}%
                        </div>
                    </div>
                        )}
                </div>
            </Card>
                )}

            {/* Options strategy */}
            {ev.optionStrategy && (
            <Card title="Options Strategy" accent={C.blue}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <div style={{ fontSize: "14px", fontWeight: 700, color: C.cyan, fontFamily: "'Syne', sans-serif" }}>
                        {ev.optionStrategy.replace(/_/g, " ")}
                    </div>
                    <div style={{ fontSize: "9px", color: C.muted, fontFamily: "monospace" }}>
                        Master bot's selected strategy · your position mirrors this
                    </div>
                </div>
            </Card>
                )}
        </div>
    );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function FollowerBotPanel({
    followerBars    = [],
    currentPrice    = 0,
    openPositions   = [],
    closedPositions = [],
    metrics         = {},
    onPauseBot,
    }) {
    const [viewMode, setViewMode] = useState("dashboard");

    const data = usePanelData({
        followerBars,
        openPositions,
        closedPositions,
        currentPrice,
        metrics,
    });

    return (
        <div style={{ fontFamily: "'DM Mono', monospace", marginBottom: "12px" }}>
            <style>{FONTS}{`
        @keyframes fbpRipple {
          0%   { transform: translate(-50%,-50%) scale(1);   opacity: 0.7; }
          100% { transform: translate(-50%,-50%) scale(2.8); opacity: 0; }
        }
        @keyframes fbpTrailFade { 0% { opacity:0.55; } 100% { opacity:0; } }
      `}</style>

            {/* Panel header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <span style={{ fontSize: "11px", fontWeight: 700, color: C.text, fontFamily: "'Syne', sans-serif", letterSpacing: "0.5px" }}>
            AGENT INTELLIGENCE
          </span>
                    <div style={{ height: "1px", width: "40px", background: C.border }} />
          <span style={{ fontSize: "10px", color: C.muted, fontFamily: "monospace" }}>
            {viewMode === "dashboard" ? "Dashboard" : "Analysis"}
          </span>
                    {/* State badge */}
          <span style={{
            fontSize: "9px", padding: "2px 7px",
            background: data.panelState === "active"     ? C.gDim
                      : data.panelState === "post_trade" ? C.cDim : C.bDim,
            color:      data.panelState === "active"     ? C.green
                      : data.panelState === "post_trade" ? C.cyan  : C.blue,
            borderRadius: "3px", fontFamily: "monospace", fontWeight: 700,
          }}>
            {data.panelState.replace("_", " ").toUpperCase()}
          </span>
                </div>

                {/* View toggle */}
                <div style={{ display: "flex", gap: "2px", padding: "3px", background: C.s2, borderRadius: "6px", border: `1px solid ${C.border}` }}>
                    {[["dashboard", "⬡ Dashboard"], ["analysis", "⊞ Analysis"]].map(([mode, label]) => (
                    <button
                        key={mode}
                        onClick={() => setViewMode(mode)}
                        style={{
                padding: "5px 14px", fontFamily: "'DM Mono', monospace",
                background:   viewMode === mode ? C.s3 : "transparent",
                border:       `1px solid ${viewMode === mode ? C.b2 : "transparent"}`,
                borderRadius: "4px",
                color:        viewMode === mode ? C.text : C.muted,
                fontSize: "10px", cursor: "pointer",
                fontWeight: viewMode === mode ? 700 : 400,
                transition: "all 0.15s",
              }}
                    >{label}</button>
                        ))}
                </div>
            </div>

            {/* Main panel */}
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: "12px", padding: "16px" }}>
                {viewMode === "dashboard"
                    ? <DashboardView data={data} />
                    : <AnalysisView  data={data} />
                    }
            </div>

            {/* Footer */}
            <div style={{ marginTop: "8px", display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontSize: "9px", color: C.subtle, fontFamily: "monospace" }}>BERBUL AI · Follower Intelligence Panel</span>
                <span style={{ fontSize: "9px", color: C.subtle, fontFamily: "monospace" }}>bars every 1m · reactive check every 5m</span>
            </div>
        </div>
    );
}